import React, { useEffect, useMemo, useState } from "react";
import BoxCard from "shared/components/box/BoxCard.js";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";
import ChartBarClubsRanking from "shared/components/charts/ChartBarClubsRanking.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import { getActiveCompetitionsByDivision } from "shared/services/api-assistant.js";
import { divisions } from "shared/utils/division.js";

const squadColor = "#4dd4ac";
const criteriaLabels = { playerCount: "Nb", b11: "B11", b11a: "B11a", b16a: "B16a", ig11: "IG11", mmr: "MMR" };

const SquadDivisionStrength = ({ team, metrics, active }) => {
  const [division, setDivision] = useState("Diamond");
  const [league, setLeague] = useState("");
  const [criteria, setCriteria] = useState("mmr");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!active) return;
    let current = true;
    setResult(null);
    setError(false);
    getActiveCompetitionsByDivision({
      params: { divisionName: division },
      handleSuccess: (response) => {
        if (current) setResult({ division, competitions: response.data.getActiveCompetitionsByDivision || [] });
      },
      handleError: () => {
        if (current) setError(true);
      },
    });
    return () => {
      current = false;
    };
  }, [active, division, retry]);

  const competitions =
    result?.division === division
      ? result.competitions.filter((competition) => competition.name?.includes("League"))
      : null;
  const clubs = useMemo(() => {
    if (!competitions) return null;
    const selected = league ? competitions.filter((competition) => competition.id === league) : competitions;
    return [
      ...new Map(
        selected.flatMap((competition) => competition.participants || []).map((club) => [club.id, club]),
      ).values(),
    ];
  }, [competitions, league]);
  const squad = metrics ? { ...metrics, id: `squad-builder:${team.id}`, name: `${team.name} (current squad)` } : null;
  const value = squad?.[criteria];
  const hasValue = Number.isFinite(value);
  const rank = clubs && hasValue ? clubs.filter((club) => (club[criteria] ?? 0) > value).length + 1 : null;

  if (!active) return null;

  return (
    <BoxCard
      className="d-flex flex-fill pb-2"
      title="Division strength"
      actions={
        <ButtonSelect
          ariaLabel="Strength metric"
          selected={criteria}
          onChange={setCriteria}
          buttons={criteriaLabels}
          className="d-flex flex-row flex-wrap border"
        />
      }
      content={
        <div className="d-flex flex-column w-100" style={{ minWidth: 0 }}>
          <div className="d-flex flex-wrap align-items-center justify-content-start gap-2 my-1 ms-2">
            <select
              className="form-select w-auto"
              aria-label="Division"
              value={division}
              onChange={(event) => {
                setDivision(event.target.value);
                setLeague("");
              }}
            >
              {divisions.map((item) => (
                <option key={item.name} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
            <select
              className="form-select w-auto"
              aria-label="League"
              value={league}
              onChange={(event) => setLeague(event.target.value)}
              disabled={!competitions?.length}
            >
              <option value="">All leagues</option>
              {competitions?.map((competition) => (
                <option key={competition.id} value={competition.id}>
                  {competition.name}
                </option>
              ))}
            </select>
          </div>
          {error ? (
            <div role="alert">
              Could not load division strength.{" "}
              <button className="btn btn-sm btn-link primary-color" onClick={() => setRetry((value) => value + 1)}>
                Retry
              </button>
            </div>
          ) : clubs === null ? (
            <div
              className="d-flex flex-column flex-grow-1 w-100"
              role="status"
              aria-label="Loading division strength"
              style={{ minHeight: "400px" }}
            >
              <LoadingSquare height="auto" />
            </div>
          ) : clubs.length === 0 ? (
            <div className="text-secondary py-4">No clubs found for this selection.</div>
          ) : (
            <div className="position-relative w-100 flex-grow-1" style={{ minHeight: "400px" }}>
              <ChartBarClubsRanking
                clubs={hasValue ? [...clubs, squad] : clubs}
                criteria={criteria}
                highlightedClubs={squad ? [squad.id] : []}
                highlightedClubColor={squadColor}
              />
            </div>
          )}
        </div>
      }
    />
  );
};

export default SquadDivisionStrength;
